import React from "react";
import {
  Input,
  InputElementProps,
  InputGroup,
  InputGroupProps,
  InputLeftAddon,
  InputLeftElement,
  InputProps,
  InputRightAddon,
  InputRightElement,
  Textarea,
  TextareaProps
} from "@chakra-ui/react";
import { generateId, useField } from "duck";
import { Types } from "../../duck";
import InputLayout from "../input-layout";

interface TextInputProps
  extends Omit<InputProps & TextareaProps, "name">,
    Types.BaseInputProps {
  type?: "text" | "email" | "password" | "tel" | "text-area";
  inputGroupProps?: InputGroupProps;
  inputLeftElementProps?: InputElementProps;
  inputRightElementProps?: InputElementProps;
  inputLeftAddonProps?: InputElementProps;
  inputRightAddonProps?: InputElementProps;
  numeric?: boolean;
  getValueLabel?(value: string): string;
}

const TextInput: React.FC<TextInputProps> = ({
  label,
  name,
  labelHidden = false,
  type = "text",
  inputLayoutProps,
  inputLeftElementProps,
  inputRightElementProps,
  inputLeftAddonProps,
  inputRightAddonProps,
  inputGroupProps,
  numeric,
  onChange,
  onBlur,
  onFocus,
  getValueLabel,
  value,
  defaultValue,
  ...rest
}) => {
  const [focused, setFocused] = React.useState(false);

  const labelId = React.useMemo(() => name || generateId(), [name]);
  const fieldProps = useField(name);
  const valueToUse = fieldProps.value === null ? "" : fieldProps.value;

  return (
    <InputLayout
      id={labelId}
      label={label}
      labelHidden={labelHidden}
      errorMessage={fieldProps.error}
      input={
        type === "text-area" ? (
          <Textarea
            name={labelId}
            defaultValue={defaultValue}
            value={value !== undefined ? value : fieldProps.value}
            onChange={onChange || fieldProps.onChange}
            ref={fieldProps.ref}
            onBlur={onBlur || fieldProps.onBlur}
            onFocus={onFocus}
            {...rest}
          />
        ) : (
          <InputGroup {...inputGroupProps}>
            {inputLeftElementProps && (
              <InputLeftElement
                pointerEvents="none"
                zIndex={1}
                {...inputLeftElementProps}
              />
            )}
            {inputLeftAddonProps && (
              <InputLeftAddon
                pointerEvents="none"
                zIndex={1}
                {...inputLeftAddonProps}
              />
            )}
            <Input
              ref={fieldProps.ref}
              type={type}
              name={labelId}
              defaultValue={defaultValue}
              value={
                valueToUse !== "" && getValueLabel && !focused
                  ? getValueLabel(valueToUse)
                  : valueToUse
              }
              onChange={e => {
                if (numeric) {
                  const newValue = e.target.value.replace(/[^\d]+/g, "");
                  const newEvent: React.ChangeEvent<HTMLInputElement> = {
                    ...e,
                    target: { ...e.target, value: newValue } as any
                  };
                  if (onChange) {
                    onChange(newEvent);
                  } else if (fieldProps.onChange) {
                    fieldProps.onChange(newEvent);
                  }
                  return;
                }

                if (onChange) {
                  onChange(e);
                } else if (fieldProps.onChange) {
                  fieldProps.onChange(e);
                }
              }}
              onFocus={event => {
                setFocused(true);
                if (onFocus) {
                  onFocus(event);
                }
              }}
              onBlur={event => {
                setFocused(false);
                if (onBlur) {
                  onBlur(event);
                } else if (fieldProps.onBlur) {
                  fieldProps.onBlur();
                }
              }}
              {...rest}
            />
            {inputRightElementProps && (
              <InputRightElement
                pointerEvents="none"
                zIndex={1}
                {...inputRightElementProps}
              />
            )}
            {inputRightAddonProps && (
              <InputRightAddon
                pointerEvents="none"
                zIndex={1}
                {...inputRightAddonProps}
              />
            )}
          </InputGroup>
        )
      }
      {...inputLayoutProps}
    />
  );
};

export default TextInput;
